import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AtlaskitBreadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { getContextBreadcrumbs, getKbPageBreadcrumbs } from './util';
import calculateBreadcrumbs from '../../utils/calculate-breadcrumbs';
import {
    COLLECTION,
    TOPIC,
    TOPIC_SET
} from '../../../contentful/content-types';

import './breadcrumbs.less';
import track from '../atlas-link/track';
import camelCase from 'lodash/camelCase';
import type { KbPageReferences } from '../../../content/types/content/kb-article';
import type { ContentfulContext } from '../../../content/types/content/context';
import { translatableText } from '../../utils/translatable-text';
import { getLocalizedHref } from '../../utils/get-localized-url';

export interface BreadcrumbsArgs {
    entryId: string;
    pageTree: any[];
    context: ContentfulContext | KbPageReferences | null;
    type: string;
}

export interface BreadcrumbItemProps {
    id: string;
    slug: string;
    title: string;
}

const BreadcrumbItems = (crumb: BreadcrumbItemProps) => {
    const [localizedSlug, setLocalizedSlug] = useState(crumb.slug);

    useEffect(() => {
        if (crumb?.slug) {
            setLocalizedSlug(getLocalizedHref(crumb.slug));
        }
    }, [crumb.slug]);

    const onClick = useCallback(
        () =>
            track(
                localizedSlug,
                crumb.title,
                camelCase(`breadcrumb ${crumb.title}`)
            ),
        [crumb.title, localizedSlug]
    );

    return (
        <BreadcrumbsItem
            truncationWidth={150}
            key={crumb.id}
            href={localizedSlug}
            text={crumb.title}
            onClick={onClick}
            testId={`breadcrumb-item-${crumb.title}`}
        />
    );
};

const Breadcrumbs = ({
    pageTree,
    entryId,
    context,
    type = ''
}: BreadcrumbsArgs): React.ReactElement => {
    const displayResourcesBreadcrumb =
        type === COLLECTION || type === TOPIC_SET || type === TOPIC;

    const isContextReference = !!context && 'id' in context;
    const isKbReference = !!context && 'kbHomePageReference' in context;

    // Initialize with the hardcoded Atlassian Support crumb
    let crumbs: BreadcrumbItemProps[] = [
        {
            id: '/',
            slug: '/',
            title: translatableText.atlassianSupport.text
        }
    ];

    crumbs = crumbs.concat(
        // Add context breadcrumbs
        isContextReference
            ? getContextBreadcrumbs(context, displayResourcesBreadcrumb)
            : [],
        // Add KB breadcrumbs
        isKbReference ? getKbPageBreadcrumbs(context) : [],
        // Add page tree breadcrumbs
        useMemo(
            () =>
                context ? calculateBreadcrumbs(pageTree, entryId, type) : [],
            [pageTree, entryId, type, context]
        )
    );

    // get JSON-LD structured data breadcrumb markup to assist with SEO
    // https://developers.google.com/search/docs/data-types/breadcrumb

    return (
        <>
            {/* @ts-ignore */}
            <div className="breadcrumbs-wrapper" data-testid="breadcrumbs">
                <AtlaskitBreadcrumbs testId="breadcrumb-container">
                    {crumbs.map((crumb) => BreadcrumbItems(crumb))}
                </AtlaskitBreadcrumbs>
            </div>
        </>
    );
};

export default Breadcrumbs;
